import { toast } from './feedback'
import { isNativeApp } from './native'
import { redirectTo } from './navigation'
import { getStorage, removeStorage } from './storage'

export interface ApiResponse<T> {
  code?: number
  msg?: string
  message?: string
  data?: T
}

export interface RequestOptions extends RequestInit {
  ignoreAuth?: boolean
  toastError?: boolean
  params?: Record<string, unknown>
  /** 请求超时（毫秒），默认 30000，与旧版 alova 全局超时一致。传 0 禁用超时。 */
  timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 30000

export function getBaseUrl() {
  if (isNativeApp() && import.meta.env.VITE_NATIVE_SERVER_BASEURL) {
    return import.meta.env.VITE_NATIVE_SERVER_BASEURL
  }
  if (import.meta.env.DEV && import.meta.env.VITE_APP_PROXY === 'true') {
    return import.meta.env.VITE_APP_PROXY_PREFIX || '/xiaozhi'
  }
  return import.meta.env.VITE_SERVER_BASEURL || '/xiaozhi'
}

export function buildUrl(path: string) {
  if (/^https?:\/\//u.test(path)) {
    return path
  }
  const base = getBaseUrl().replace(/\/$/u, '')
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalized}`
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const query = options.params ? toQueryString(options.params) : ''
  const requestPath = query ? `${path}${path.includes('?') ? '&' : '?'}${query}` : path
  const headers = new Headers(options.headers)
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json, text/plain, */*')
  }
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (!options.ignoreAuth) {
    const token = getStorage('token')
    if (!token) {
      await redirectTo('/pages/login/index')
      throw new Error('未登录')
    }
    headers.set('Authorization', `Bearer ${token}`)
  }

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const timeoutController = options.signal || !timeoutMs ? null : new AbortController()
  const timeoutId = timeoutController
    ? setTimeout(() => timeoutController.abort(), timeoutMs)
    : undefined

  let response: Response
  try {
    response = await fetch(buildUrl(requestPath), {
      ...options,
      headers,
      signal: options.signal ?? timeoutController?.signal,
    })
  }
  catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      const message = '请求超时，请检查网络后重试'
      if (options.toastError !== false) {
        toast(message, 'fail')
      }
      throw new Error(message)
    }
    throw error
  }
  finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  }

  const contentType = response.headers.get('content-type') || ''
  const payload = contentType.includes('application/json')
    ? ((await response.json()) as ApiResponse<T>)
    : ({ data: (await response.text()) as T } as ApiResponse<T>)

  if (response.status === 401) {
    removeStorage('token')
    await redirectTo('/pages/login/index')
    throw new Error('登录状态已失效')
  }

  if (!response.ok) {
    const message = payload.msg || payload.message || `HTTP ${response.status}`
    if (options.toastError !== false) {
      toast(message, 'fail')
    }
    throw new Error(message)
  }

  if (typeof payload.code === 'number' && payload.code !== 0 && payload.code !== 200) {
    const message = payload.msg || payload.message || '请求失败'
    if (options.toastError !== false) {
      toast(message, 'fail')
    }
    throw new Error(message)
  }

  return payload.data as T
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE', body: JSON.stringify(body ?? {}) }),
}

function toQueryString(params: Record<string, unknown>) {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') {
      return
    }
    search.set(key, String(value))
  })
  return search.toString()
}
